import React, { useCallback, useEffect, useState } from 'react';
import AdminShell, { useAdminKey } from '../components/admin/AdminShell';

const ISSUE_LABEL = {
  location: 'Location',
  fees: 'Fees / tuition costs',
  curriculum: 'Curriculum',
  closed: 'School is closed or no longer operating',
  other: 'Other',
};

const badgeClass = (issue) => {
  switch (issue) {
    case 'location':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'fees':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'curriculum':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'closed':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-[#FFF5EB] text-[#E85D35] border-[#E8DED3]';
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const SchoolFeedbackBody = () => {
  const { adminKey } = useAdminKey();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/school-feedback`, {
        headers: { 'X-Admin-Key': adminKey },
      });
      if (!res.ok) {
        throw new Error(res.status === 401 ? 'Unauthorized' : `Failed (${res.status})`);
      }
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message || 'Could not load feedback');
    } finally {
      setLoading(false);
    }
  }, [API_URL, adminKey]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-end mb-4">
        <button
          onClick={fetchFeedback}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E8DED3] text-[#2C2823] hover:bg-[#FFF5EB]"
          data-testid="admin-school-feedback-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-[#6B6560]">Loading…</p>}
      {error && (
        <p className="text-sm text-red-600 font-medium" data-testid="admin-school-feedback-error">
          {error}
        </p>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-[#6B6560]" data-testid="admin-school-feedback-empty">
          No flags submitted yet.
        </p>
      )}

      <div className="space-y-3" data-testid="admin-school-feedback-list">
        {items.map((item) => (
          <div
            key={item.feedback_id}
            className="bg-white border border-[#E8DED3] rounded-xl p-4 sm:p-5"
            data-testid={`admin-school-feedback-row-${item.feedback_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <p className="font-bold text-[#2C2823] truncate">{item.school_name}</p>
                <p className="text-xs text-[#9B8F88] mt-0.5">
                  {formatDate(item.submitted_at)}
                  {item.report_id ? ` · report ${item.report_id}` : ''}
                </p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeClass(item.issue_type)}`}
              >
                {ISSUE_LABEL[item.issue_type] || item.issue_type}
              </span>
            </div>
            {item.correction ? (
              <p className="text-sm text-[#2C2823] leading-relaxed whitespace-pre-wrap">
                {item.correction}
              </p>
            ) : (
              <p className="text-sm italic text-[#9B8F88]">No correction text provided.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminSchoolFeedbackPage = () => (
  <AdminShell
    title="School data flags"
    subtitle="Parent-reported inaccuracies on school cards · manual review only"
  >
    <SchoolFeedbackBody />
  </AdminShell>
);

export default AdminSchoolFeedbackPage;
